import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TranslationProvider } from './contexts/TranslationContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { CookieConsentProvider, useCookieConsent } from './contexts/CookieConsentContext';
import { identifyClarityUser, initializeClarity, updateClarityConsent } from './utils/clarity';
import { initializeAnalytics, trackPageView, setGAUserId, setGAUserProperties } from './utils/analytics';
import { CookieConsent } from './components/cookies/CookieConsent';
import { Layout } from './components/layout/Layout';
import { LocationSelector } from './components/auth/LocationSelector';
import { RoleProtectedRoute, AccessDenied, KlijentliAdminRoute } from './components/auth/RoleProtectedRoute';
import { AdminLayout } from './components/admin/AdminLayout';

// Lazy load pages for code splitting and performance optimization
const LandingPage = lazy(() => import('./pages/LandingPage'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage').then(m => ({ default: m.LoginPage })));
const SignupPage = lazy(() => import('./pages/auth/SignupPage').then(m => ({ default: m.SignupPage })));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })));
const ClientsPage = lazy(() => import('./pages/clients/ClientsPage').then(m => ({ default: m.ClientsPage })));
const ClientForm = lazy(() => import('./pages/ClientForm'));
const ClientDetail = lazy(() => import('./pages/ClientDetail'));
const TreatmentsPage = lazy(() => import('./pages/treatments/TreatmentsPage').then(m => ({ default: m.TreatmentsPage })));
const TreatmentDetail = lazy(() => import('./pages/TreatmentDetail'));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage').then(m => ({ default: m.ReportsPage })));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage').then(m => ({ default: m.SettingsPage })));
const CompanySettingsPage = lazy(() => import('./pages/company/CompanySettingsPage').then(m => ({ default: m.CompanySettingsPage })));
const BusinessHoursPage = lazy(() => import('./pages/company/BusinessHoursPage').then(m => ({ default: m.BusinessHoursPage })));
const DefaultSettingsPage = lazy(() => import('./pages/company/DefaultSettingsPage').then(m => ({ default: m.DefaultSettingsPage })));
const InvitationManagementPage = lazy(() => import('./pages/invitations/InvitationManagementPage').then(m => ({ default: m.InvitationManagementPage })));
const InvitationAcceptPage = lazy(() => import('./pages/invitations/InvitationAcceptPage').then(m => ({ default: m.InvitationAcceptPage })));
const EmailVerificationPage = lazy(() => import('./pages/auth/EmailVerificationPage'));
const PasswordResetPage = lazy(() => import('./pages/auth/PasswordResetPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const TreatmentTypes = lazy(() => import('./pages/TreatmentTypes'));
const TreatmentTypeForm = lazy(() => import('./pages/TreatmentTypeForm'));
const TreatmentForm = lazy(() => import('./pages/TreatmentForm'));
const Locations = lazy(() => import('./pages/Locations'));
const LocationForm = lazy(() => import('./pages/LocationForm'));
const DocumentationPage = lazy(() => import('./pages/DocumentationPage').then(m => ({ default: m.DocumentationPage })));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage').then(m => ({ default: m.FeaturesPage })));
const PricingPage = lazy(() => import('./pages/PricingPage').then(m => ({ default: m.PricingPage })));
const SecurityPage = lazy(() => import('./pages/SecurityPage').then(m => ({ default: m.SecurityPage })));
const HelpCenterPage = lazy(() => import('./pages/HelpCenterPage').then(m => ({ default: m.HelpCenterPage })));
const ContactUsPage = lazy(() => import('./pages/ContactUsPage').then(m => ({ default: m.ContactUsPage })));
const StatusPage = lazy(() => import('./pages/StatusPage').then(m => ({ default: m.StatusPage })));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage').then(m => ({ default: m.TermsOfServicePage })));
const RefundPolicyPage = lazy(() => import('./pages/RefundPolicyPage').then(m => ({ default: m.RefundPolicyPage })));
const HIPAACompliancePage = lazy(() => import('./pages/HIPAACompliancePage').then(m => ({ default: m.HIPAACompliancePage })));
const SubscriptionPage = lazy(() => import('./pages/billing/SubscriptionPage').then(m => ({ default: m.SubscriptionPage })));
const InvoicesPage = lazy(() => import('./pages/billing/InvoicesPage').then(m => ({ default: m.InvoicesPage })));
const PaymentSuccessPage = lazy(() => import('./pages/billing/PaymentSuccessPage').then(m => ({ default: m.PaymentSuccessPage })));
// Admin pages
const AdminDashboardPage = lazy(() => import('./pages/admin/DashboardPage').then(m => ({ default: m.default })));
const OrganizationsPage = lazy(() => import('./pages/admin/organizations/OrganizationsPage').then(m => ({ default: m.default })));
const AdminLocationsPage = lazy(() => import('./pages/admin/locations/LocationsPage').then(m => ({ default: m.default })));
const AdminUsersPage = lazy(() => import('./pages/admin/users/UsersPage').then(m => ({ default: m.default })));
const AdminClientsPage = lazy(() => import('./pages/admin/clients/ClientsPage').then(m => ({ default: m.default })));
const AdminTreatmentsPage = lazy(() => import('./pages/admin/treatments/TreatmentsPage').then(m => ({ default: m.default })));
const NotesPage = lazy(() => import('./pages/admin/notes/NotesPage').then(m => ({ default: m.default })));
const TicketsPage = lazy(() => import('./pages/admin/tickets/TicketsPage').then(m => ({ default: m.default })));
const TicketDetailPage = lazy(() => import('./pages/admin/tickets/TicketDetailPage').then(m => ({ default: m.default })));
const WorkflowsPage = lazy(() => import('./pages/admin/tickets/WorkflowsPage').then(m => ({ default: m.default })));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
  </div>
);

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
}

// Admin Route Component
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <KlijentliAdminRoute fallback={<AccessDenied message="Klijentli admin access required." />}>
      <AdminLayout>{children}</AdminLayout>
    </KlijentliAdminRoute>
  );
}

// Public Route Component (redirects to dashboard if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Landing Route Component (shows landing page for unauthenticated users, dashboard for authenticated)
function LandingRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <LandingPage />;
}

function AppRoutes() {
  const { showLocationSelector, user, tenant, isAuthenticated } = useAuth();
  const { preferences, hasConsent } = useCookieConsent();
  const location = useLocation();

  // Initialize Clarity when consent is given
  useEffect(() => {
    if (hasConsent && preferences) {
      initializeClarity('urqh9gg53r', preferences);
    }
  }, [hasConsent, preferences]);

  // Initialize GTM and GA when consent is given
  useEffect(() => {
    if (hasConsent && preferences) {
      initializeAnalytics(preferences);
    }
  }, [hasConsent, preferences]);

  // Update Clarity consent when preferences change
  useEffect(() => {
    if (hasConsent && preferences) {
      updateClarityConsent(preferences);
    }
  }, [preferences, hasConsent]);

  // Track route changes and identify user in Clarity on each page
  useEffect(() => {
    if (isAuthenticated && user && tenant && hasConsent && preferences?.analytics_Storage === 'granted') {
      identifyClarityUser(user, tenant, location.pathname);
    }
  }, [location.pathname, isAuthenticated, user?.id, tenant?.id, hasConsent, preferences?.analytics_Storage]);

  // Track page views in GA on route changes (server-side)
  useEffect(() => {
    if (hasConsent && preferences?.analytics_Storage === 'granted') {
      const userId = isAuthenticated && user?.id ? user.id.toString() : null;
      const userProperties = isAuthenticated && user && tenant ? {
        email: user.email || '',
        name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || '',
        organization: tenant.name || '',
        organization_id: tenant.id || '',
      } : {};
      
      trackPageView(location.pathname, document.title, userId, userProperties);
    }
  }, [location.pathname, hasConsent, preferences?.analytics_Storage, isAuthenticated, user?.id, user?.email, tenant?.id, tenant?.name]);

  // Set GA user properties when authenticated (stored for subsequent events)
  useEffect(() => {
    if (isAuthenticated && user && tenant && hasConsent && preferences?.analytics_Storage === 'granted') {
      // Set user ID
      if (user.id) {
        setGAUserId(user.id.toString());
      }

      // Set user properties
      setGAUserProperties({
        email: user.email || '',
        name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || '',
        organization: tenant.name || '',
        organization_id: tenant.id || '',
      });
    }
  }, [isAuthenticated, user?.id, user?.email, tenant?.id, tenant?.name, hasConsent, preferences?.analytics_Storage]);

  // Show location selector if user is logged in but hasn't selected a location
  if (showLocationSelector) {
    return <LocationSelector />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
      {/* Landing Page */}
      <Route path="/" element={<LandingRoute />} />
      
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <SignupPage />
          </PublicRoute>
        }
      />
      
      {/* Email Routes */}
      <Route
        path="/verify-email"
        element={<EmailVerificationPage />}
      />
      <Route
        path="/reset-password"
        element={<PasswordResetPage />}
      />
      
      {/* Documentation Route - Public */}
      <Route
        path="/documentation"
        element={<DocumentationPage />}
      />
      
      {/* Public Information Pages */}
      <Route
        path="/features"
        element={<FeaturesPage />}
      />
      <Route
        path="/pricing"
        element={<PricingPage />}
      />
      <Route
        path="/security"
        element={<SecurityPage />}
      />
      <Route
        path="/help"
        element={<HelpCenterPage />}
      />
      <Route
        path="/contact"
        element={<ContactUsPage />}
      />
      <Route
        path="/status"
        element={<StatusPage />}
      />
      <Route
        path="/privacy"
        element={<PrivacyPolicyPage />}
      />
      <Route
        path="/terms"
        element={<TermsOfServicePage />}
      />
      <Route
        path="/refund"
        element={<RefundPolicyPage />}
      />
      <Route
        path="/hipaa"
        element={<HIPAACompliancePage />}
      />
      
      {/* Invitation Routes */}
      <Route
        path="/invite/accept/:token"
        element={<InvitationAcceptPage />}
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <ClientsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/new"
        element={
          <ProtectedRoute>
            <ClientForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/:id"
        element={
          <ProtectedRoute>
            <ClientDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/:id/edit"
        element={
          <ProtectedRoute>
            <ClientForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/treatments"
        element={
          <ProtectedRoute>
            <TreatmentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/treatments/new"
        element={
          <ProtectedRoute>
            <TreatmentForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/treatments/:id"
        element={
          <ProtectedRoute>
            <TreatmentDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/treatments/:id/edit"
        element={
          <ProtectedRoute>
            <TreatmentForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/treatment-types"
        element={
          <ProtectedRoute>
            <TreatmentTypes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/treatment-types/new"
        element={
          <ProtectedRoute>
            <TreatmentTypeForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/treatment-types/:id/edit"
        element={
          <ProtectedRoute>
            <TreatmentTypeForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/locations"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute permission="canManageLocations" fallback={<AccessDenied message="You don't have permission to manage locations." />}>
              <Locations />
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/locations/new"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute permission="canManageLocations" fallback={<AccessDenied message="You don't have permission to create locations." />}>
              <LocationForm />
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/locations/:id/edit"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute permission="canManageLocations" fallback={<AccessDenied message="You don't have permission to edit locations." />}>
              <LocationForm />
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />
      {/* Redirect old appointments routes to treatments */}
      <Route
        path="/appointments"
        element={<Navigate to="/treatments" replace />}
      />
      <Route
        path="/waiting-list"
        element={<Navigate to="/treatments" replace />}
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute permission="canViewReports" fallback={<AccessDenied message="You don't have permission to view reports." />}>
              <ReportsPage />
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute permission="canViewSettings" fallback={<AccessDenied message="You don't have permission to access settings." />}>
              <SettingsPage />
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/company-settings"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute permission="canViewSettings" fallback={<AccessDenied message="You don't have permission to access company settings." />}>
              <CompanySettingsPage />
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/company/business-hours"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute permission="canEditSettings" fallback={<AccessDenied message="You don't have permission to manage business hours." />}>
              <BusinessHoursPage />
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/company/defaults"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute permission="canEditSettings" fallback={<AccessDenied message="You don't have permission to manage default settings." />}>
              <DefaultSettingsPage />
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute permission="canCreateUsers" fallback={<AccessDenied message="You don't have permission to manage team members." />}>
              <InvitationManagementPage />
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      
      {/* Billing Routes */}
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute permission="canManageSubscription" fallback={<AccessDenied message="You don't have permission to manage subscription." />}>
              <SubscriptionPage />
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/invoices"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute permission="canManageBilling" fallback={<AccessDenied message="You don't have permission to view invoices." />}>
              <InvoicesPage />
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/success"
        element={
          <ProtectedRoute>
            <PaymentSuccessPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminDashboardPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/organizations"
        element={
          <AdminRoute>
            <OrganizationsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/locations"
        element={
          <AdminRoute>
            <AdminLocationsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <AdminRoute>
            <AdminUsersPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/clients"
        element={
          <AdminRoute>
            <AdminClientsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/treatments"
        element={
          <AdminRoute>
            <AdminTreatmentsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/notes"
        element={
          <AdminRoute>
            <NotesPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/tickets"
        element={
          <AdminRoute>
            <TicketsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/tickets/:id"
        element={
          <AdminRoute>
            <TicketDetailPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/workflows"
        element={
          <AdminRoute>
            <WorkflowsPage />
          </AdminRoute>
        }
      />

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TranslationProvider>
          <CookieConsentProvider>
            <AuthProvider>
              <CurrencyProvider>
                <Router>
                  <AppRoutes />
                  <CookieConsent />
                  <Toaster
                    position="top-right"
                    toastOptions={{
                      duration: 4000,
                      style: {
                        background: '#363636',
                        color: '#fff',
                      },
                      success: {
                        duration: 3000,
                        iconTheme: {
                          primary: '#22c55e',
                          secondary: '#fff',
                        },
                      },
                      error: {
                        duration: 5000,
                        iconTheme: {
                          primary: '#ef4444',
                          secondary: '#fff',
                        },
                      },
                    }}
                  />
                </Router>
              </CurrencyProvider>
            </AuthProvider>
          </CookieConsentProvider>
        </TranslationProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;